<template>
    <MainViewCard>
        <div class="px-6 py-6">
            <h4 class="text-lg font-medium my-auto flex-1">{{ data.title }}</h4>
            <p v-if="data.subtitle" class="text-sm text-gray-500">{{ data.subtitle }}</p>
        </div>
        <CustomTable :headers="computedHeaders" :items="data.items" :row-class="getRowBackgroundClass">
            <template #action="{ header, getWidth, getItemProperty, getHeaderClass, item }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words min-h-6.5`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    <div class="flex items-center gap-2">
                        <CustomCheckbox v-if="item.row_type === 'difference'" :checked="hasComment" @update:checked="onCheckboxChange($event)" />
                        <span>{{ getItemProperty(header, item) }}<sup v-if="item.weight_derived">(*)</sup></span>
                    </div>
                </td>
            </template>
            <template #amount="{ header, getWidth, getItemProperty, getHeaderClass, item }">
                <td
                    :key="header.value"
                    :class="`${getHeaderClass(header, false)} ${hasActivityRecords(item) ? 'underline' : ''} my-auto break-words min-h-6.5`"
                    :style="`max-width: ${getWidth(header)}px; width: 100%;`"
                >
                    <ActivityTableModal
                        :title="$t('views.reports.reportsPage.tables.departures.activitiesModalHeader')"
                        :record-type-ids="item.record_type_ids"
                        :fct-object-ids="item.fct_object_ids"
                        :records="records"
                        @fetch-records="$emit('fetch-records')"
                    >
                        <template #default="scope">
                            <span :class="{ 'cursor-pointer': hasActivityRecords(item) }" @click="hasActivityRecords(item) && scope.open()">{{ getItemProperty(header, item) }}</span>
                        </template>
                    </ActivityTableModal>
                </td>
            </template>
            <template #weight="{ header, getWidth, getItemProperty, getHeaderClass, item }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words min-h-6.5`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    {{ getItemProperty(header, item) }}
                </td>
            </template>
            <template #average_weight="{ header, getWidth, getItemProperty, getHeaderClass, item }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words min-h-6.5`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    {{ getItemProperty(header, item) }}
                </td>
            </template>
            <template #actions="{ header, getWidth, getHeaderClass, item }">
                <td v-if="item.row_type === 'difference'" :key="header.value" :class="`${getHeaderClass(header, false)} my-auto min-h-6.5`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    <DropdownMenu :modal="false">
                        <DropdownMenuTrigger as-child>
                            <IconButton variant="ghost" size="icon" class="rounded-full h-6 w-6 -mr-[9px]">
                                <span class="sr-only">{{ $t('views.reports.reportsPage.tables.actions') }}</span>
                                <EllipsisVertical class="size-4" aria-hidden="true" />
                            </IconButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem class="cursor-pointer font-medium" @click="openModal">
                                {{ $t('views.reports.reportsPage.tables.edit') }}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </td>
                <td v-else :key="`${header.value}-empty`" :class="`${getHeaderClass(header, false)} my-auto min-h-6.5`" :style="`max-width: ${getWidth(header)}px; width: 100%;`"></td>
            </template>
            <template #append-rows="{ getWidth, getItemProperty, getHeaderClass }">
                <tr class="rounded flex py-4 px-6 gap-x-4 odd:bg-dark-gray-200 font-semibold">
                    <td v-for="header of computedHeaders" :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words min-h-6.5`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                        {{ getItemProperty(header, data.total) }}
                    </td>
                </tr>
            </template>
        </CustomTable>
        <p v-if="showFootnote" class="px-6 pb-6 text-sm text-gray-500">(*) {{ data.footnote }}</p>

        <ApproveDifferenceModal ref="approveModal" :existing-comment="commentDifference" @save="onCommentSaved" />
    </MainViewCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { EllipsisVertical } from 'lucide-vue-next';
import CustomTable from '@/components/ui/CustomTable.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import ActivityTableModal from '@/views/dashboard/reports/reportsPage/ActivityTableModal.vue';
import ApproveDifferenceModal from '@/views/dashboard/reports/reportsPage/ApproveDifferenceModal.vue';
import { Button as IconButton } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TableHeader, Record, ReportTable, ReportTableRow } from '@/types';

export default defineComponent({
    components: {
        CustomTable,
        MainViewCard,
        CustomCheckbox,
        ActivityTableModal,
        ApproveDifferenceModal,
        IconButton,
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
        EllipsisVertical,
    },
    props: {
        data: {
            type: Object as () => ReportTable,
            required: true,
        },
        records: {
            type: Array as () => Record[],
            default: () => [],
        },
        commentDifference: {
            type: String,
            default: '',
        },
    },
    emits: ['fetch-records', 'update-comment'],
    computed: {
        hasComment(): boolean {
            return !!this.commentDifference?.trim();
        },
        // Only show the (*) footnote when a row actually carries the marker
        showFootnote(): boolean {
            return !!this.data.footnote && this.data.items.some((item) => item.weight_derived);
        },
        computedHeaders(): TableHeader[] {
            return [
                {
                    text: this.$t('views.reports.reportsPage.tables.departures.headers.action'),
                    value: 'action',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: this.$t('views.reports.reportsPage.tables.departures.headers.amount'),
                    value: 'amount',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: this.$t('views.reports.reportsPage.tables.departures.headers.weight'),
                    value: 'weight',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: this.$t('views.reports.reportsPage.tables.departures.headers.averageWeight'),
                    value: 'average_weight',
                    align: 'left',
                    sortable: true,
                    headerClass: 'xl:min-w-36',
                },
                {
                    text: '',
                    value: 'actions',
                    align: 'right',
                    sortable: false,
                    headerClass: 'w-12 max-w-12',
                },
            ];
        },
    },
    methods: {
        hasActivityRecords(item: ReportTableRow): boolean {
            return Boolean(item.record_type_ids?.length || item.fct_object_ids?.length);
        },
        getRowBackgroundClass(item: ReportTableRow): string {
            if (item.row_type === 'difference' && this.hasComment) {
                return 'bg-primary-300 bg-opacity-30';
            }
            return '';
        },
        openModal() {
            (this.$refs.approveModal as any)?.open();
        },
        onCheckboxChange(checked: boolean) {
            if (checked) {
                this.openModal();
            } else {
                this.$emit('update-comment', '');
            }
        },
        onCommentSaved(comment: string) {
            this.$emit('update-comment', comment);
        },
    },
});
</script>
